import React, { Fragment, useEffect, useState } from "react";
import logo from "../../../assets/media/logo.png";
import GamingBoardBahamasLogo from "../../../assets/media/GamingBoardBahamasLogo.png";
import iconMark from "../../../assets/media/icon-mark.png";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useFormik } from "formik";
import GBInput from "../../input";
import * as Yup from "yup";
import {
  clearSignupData,
  sendSignupOtp,
  signUp,
} from "../../../store/reducers/signUpSlice";
import {
  applyAuthSession,
  clearAuthUser,
  loginUser,
} from "../../../store/reducers/authSlice";
import { showError, showSuccess } from "../../../helpers/toast";
import {
  formatMfaCountdown,
  getMfaCodeSecondsLeft,
} from "../../../helpers/mfaCodeTimer";

/**
 * Signup SMS verification step (POST /users/signup/send-otp → otp on /users/signup).
 * Skipped entirely when send-otp returns otp_required: false (handled on step 2).
 */
const SignUp3 = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const signUpUser = useSelector((state) => state.signUp);
  const loading = useSelector((state) => state.signUp.loading);
  const user = useSelector((state) => state.auth.user);
  const mfa = useSelector((state) => state.auth.mfa);
  const signupData = useSelector((state) => state?.signUp?.data?.payload);
  const [resendLoading, setResendLoading] = useState(false);
  const [isInvalidSignupCode, setIsInvalidSignupCode] = useState(false);
  // Timer starts as soon as OTP screen loads; falls back to code_expires_in or 10 min
  const [expiryAt, setExpiryAt] = useState(() => {
    if (signUpUser?.codeExpiresAt) return signUpUser.codeExpiresAt;
    const sec = Number(signUpUser?.codeExpiresIn);
    return new Date(
      Date.now() + (Number.isFinite(sec) && sec > 0 ? sec : 600) * 1000
    ).toISOString();
  });
  const [secondsLeft, setSecondsLeft] = useState(() =>
    getMfaCodeSecondsLeft(
      signUpUser?.codeExpiresAt ||
        new Date(
          Date.now() +
            (Number(signUpUser?.codeExpiresIn) > 0
              ? Number(signUpUser.codeExpiresIn)
              : 600) *
              1000
        ).toISOString()
    )
  );

  const formik = useFormik({
    initialValues: { otp: "" },
    validationSchema: Yup.object({
      otp: Yup.string()
        .required("Verification code is required.")
        .matches(/^\d+$/, "Enter the digits-only code from your SMS"),
    }),
    onSubmit: async (
      formValues,
      { setFieldError, setFieldValue, setFieldTouched }
    ) => {
      if (!signUpUser?.signupcode) {
        showError(
          "Signup session expired. Please use your invitation link again."
        );
        navigate("/");
        return;
      }

      try {
        const signupResponse = await dispatch(
          signUp({
            userData: {
              signupcode: signUpUser.signupcode,
              password: signUpUser.password,
              full_name: signUpUser.full_name,
              phone: signUpUser.phone,
              companyRegistrationNo: signUpUser.companyRegistrationNo,
              otp: String(formValues.otp).trim(),
            },
          })
        ).unwrap();

        const password = signUpUser.password;
        const userEmail =
          signupResponse?.user?.email ||
          signUpUser?.message?.user?.email ||
          user?.email;

        if (signupResponse?.access_token) {
          dispatch(applyAuthSession(signupResponse));
          dispatch(clearSignupData());
          navigate("/");
          return;
        }

        if (userEmail && password) {
          const loginResult = await dispatch(
            loginUser({
              emailOrPhone: userEmail?.toLowerCase()?.trim(),
              password,
            })
          );

          if (loginUser.fulfilled.match(loginResult)) {
            if (loginResult.payload?.__mfa) {
              navigate("/login-mfa");
              return;
            }
            if (loginResult.payload?.access_token) {
              dispatch(clearSignupData());
              navigate("/");
              return;
            }
          }

          dispatch(clearAuthUser());
          dispatch(clearSignupData());
          showSuccess(
            "Registration complete. Sign in once your account is activated."
          );
          navigate("/");
          return;
        }

        dispatch(clearAuthUser());
        dispatch(clearSignupData());
        navigate("/");
      } catch (err) {
        const msg = err?.message || "Failed to complete signup";
        if (err?.invalidSignupCode) {
          setIsInvalidSignupCode(true);
          return;
        }
        if (err?.otpRequired || err?.invalidOtp || err?.otpExpired) {
          setFieldValue("otp", "", false);
          setFieldTouched("otp", true, false);
          setFieldError(
            "otp",
            err?.otpExpired
              ? msg || "Verification code expired. Request a new code."
              : msg || "Invalid verification code"
          );
          return;
        }
        if (err?.passwordError) {
          showError(msg);
          navigate("/");
          return;
        }
        if (
          (err?.status === 400 || err?.status === 401 || err?.status === 422) &&
          /otp|verification code|code/i.test(String(msg))
        ) {
          setFieldValue("otp", "", false);
          setFieldTouched("otp", true, false);
          setFieldError("otp", msg);
          return;
        }
        showError(msg);
      }
    },
  });

  const { values, handleChange, handleSubmit, errors, touched, setFieldValue } =
    formik;

  useEffect(() => {
    if (mfa?.token) return;
    if (!signUpUser?.signupcode || !signUpUser?.password || !signUpUser?.phone) {
      navigate("/");
    }
  }, [
    navigate,
    mfa?.token,
    signUpUser?.signupcode,
    signUpUser?.password,
    signUpUser?.phone,
  ]);

  useEffect(() => {
    if (user?.access_token) {
      navigate("/");
    }
  }, [navigate, user?.access_token]);

  useEffect(() => {
    if (mfa?.token) {
      navigate("/login-mfa");
    }
  }, [mfa?.token, navigate]);

  // Keep local expiry in sync when send-otp / resend returns a new deadline
  useEffect(() => {
    if (signUpUser?.codeExpiresAt) {
      setExpiryAt(signUpUser.codeExpiresAt);
      return;
    }
    const sec = Number(signUpUser?.codeExpiresIn);
    if (Number.isFinite(sec) && sec > 0) {
      setExpiryAt(new Date(Date.now() + sec * 1000).toISOString());
    }
  }, [signUpUser?.codeExpiresAt, signUpUser?.codeExpiresIn]);

  useEffect(() => {
    if (!expiryAt) {
      setSecondsLeft(0);
      return undefined;
    }
    const tick = () => {
      setSecondsLeft(getMfaCodeSecondsLeft(expiryAt));
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [expiryAt]);

  // Resend stays disabled until the countdown hits zero
  const canResend =
    secondsLeft <= 0 && !signUpUser?.resendBlocked && !resendLoading;

  const handleResendOtp = async () => {
    if (!canResend) return;
    if (!signUpUser?.signupcode || !signUpUser?.phone) {
      showError(
        "Signup session expired. Please use your invitation link again."
      );
      navigate("/");
      return;
    }
    setResendLoading(true);
    try {
      await dispatch(
        sendSignupOtp({
          signupcode: signUpUser.signupcode,
          phone: signUpUser.phone,
        })
      ).unwrap();
      setFieldValue("otp", "");
      showSuccess("OTP resent to your phone number");
    } catch (err) {
      if (err?.invalidSignupCode) {
        setIsInvalidSignupCode(true);
        return;
      }
      if (err?.invalidPhone) {
        showError(err.message);
        navigate("/signup-step2");
        return;
      }
      showError(
        err?.message || "Failed to resend OTP. Please try again."
      );
    } finally {
      setResendLoading(false);
    }
  };

  const phoneHint = signUpUser?.phoneHint
    ? ` ${signUpUser.phoneHint}`
    : "";
  const busy = loading || resendLoading;

  return (
    <div className="App">
      <div className="container">
        <div className="landingWrapper">
          <div className="landingRow">
            <div className="landingCol">
              <div className="fieldsWrapper">
                <img src={logo} className="logo" alt="logo" />
                {isInvalidSignupCode ? (
                  <>
                    <h2>Invitation link expired</h2>
                    <p>
                      This signup link is no longer valid. Please ask your
                      administrator for a new invite.
                    </p>
                    <div className="gb-form-footer">
                      <Link to="/" className="form-submit">
                        Back to Sign In
                      </Link>
                    </div>
                  </>
                ) : (
                  <>
                    <h2>Verify your phone</h2>
                    <p>
                      We sent a code to{phoneHint || " your phone number"}.
                      Enter it below to finish signing up.
                    </p>

                    <form className="gb-login-form" onSubmit={handleSubmit}>
                      <Fragment>
                        <GBInput
                          name="otp"
                          type="text"
                          label="Verification code"
                          value={values.otp}
                          placeholder="Enter SMS code"
                          setValue={handleChange}
                          iconSrc={iconMark}
                        />
                        {(touched.otp || errors.otp) && errors.otp && (
                          <div className="error-message error-message-wrap">
                            {errors.otp}
                          </div>
                        )}
                      </Fragment>

                      <div
                        className="gb-field-wrap"
                        style={{
                          marginTop: "8px",
                          marginBottom: "4px",
                          minHeight: "24px",
                        }}
                      >
                        {signUpUser?.resendBlocked ? (
                          <p
                            style={{
                              margin: 0,
                              fontSize: "14px",
                              color: "#082a51",
                            }}
                          >
                            Too many verification codes requested. Please try
                            again later.
                          </p>
                        ) : secondsLeft > 0 ? (
                          <p
                            style={{
                              margin: 0,
                              fontSize: "14px",
                              color: "#082a51",
                            }}
                            aria-live="polite"
                          >
                            Code expires in{" "}
                            <strong>{formatMfaCountdown(secondsLeft)}</strong>
                            <span
                              style={{
                                display: "block",
                                marginTop: "4px",
                                color: "#6b7c93",
                                fontSize: "13px",
                              }}
                            >
                              Resend OTP will be available when the timer ends.
                            </span>
                          </p>
                        ) : (
                          <button
                            type="button"
                            className="form-submit"
                            onClick={handleResendOtp}
                            disabled={!canResend || busy}
                            style={{
                              width: "100%",
                              marginTop: "4px",
                              background: busy ? "#ccc" : "#114df2",
                              border: busy
                                ? "solid 1px #ccc"
                                : "solid 1px #1890ff",
                              boxShadow:
                                "0px 2px 0px 0px rgba(0, 0, 0, 0.04)",
                            }}
                          >
                            {resendLoading ? "Sending..." : "Resend OTP"}
                          </button>
                        )}
                      </div>

                      <div className="form-check">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          value=""
                          id="flexCheckChecked"
                          defaultChecked
                          readOnly
                        />
                        <label
                          className="form-check-label"
                          htmlFor="flexCheckChecked"
                        >
                          I agree to <a href="#">Terms of Service</a> &{" "}
                          <a href="#">Privacy Policy</a>
                        </label>
                      </div>
                      <div className="gb-form-footer">
                        <button
                          type="submit"
                          style={{
                            background:
                              busy || !values.otp ? "#ccc" : undefined,
                            border:
                              busy || !values.otp
                                ? "solid 1px #ccc"
                                : undefined,
                          }}
                          disabled={busy || !values.otp}
                          className="form-submit"
                        >
                          {loading ? (
                            <>
                              <div
                                className="text-xs spinner-border text-light"
                                style={{ width: "1rem", height: "1rem" }}
                                role="status"
                              ></div>
                              <span className="sr-only ml-5"> Loading...</span>
                            </>
                          ) : (
                            "Sign Up"
                          )}
                        </button>
                      </div>
                    </form>
                    <div className="gb-step-list">
                      <span className="current"></span>
                      <span className="current"></span>
                      <span className="current"></span>
                    </div>
                  </>
                )}
              </div>
            </div>
            <div className="landingCol">
              <div className="imageWrapper">
                <div className="loginBox">
                  <p>
                    Already have account? <Link to={"/"}>Sign In</Link>
                  </p>
                </div>
                <img
                  width={"300px"}
                  height={"300px"}
                  src={GamingBoardBahamasLogo}
                  className="booksAnimation"
                  alt=""
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignUp3;
